// DateRangePicker Component

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'

import Util from '../../../lib/util.jsx'
import DateUtil from '../../../lib/dateutil.jsx'
import Header from './daterange_header.jsx'
import Calendar from './daterange_calendar.jsx'
import IntlUtil from '../../../lib/intlutil.jsx'

class DateRangePicker extends Component {
  constructor (props) {
    super(props)

    const activeDate = props.startDate
      ? props.startDate
      : props.endDate
        ? props.endDate
        : new Date()

    this.state = {
      active: false,
      date: new Date(activeDate).getTime(),
      selectionStart: new Date(props.startDate).getTime() || 0,
      selectionEnd: new Date(props.endDate).getTime() || 0,
      startDate: new Date(props.startDate) || null,
      endDate: new Date(props.endDate) || null
    }

    this._documentListener = this._handleDocumentClick.bind(this)
  }

  static get propTypes () {
    return {
      active: PropTypes.bool,
      startDate: PropTypes.any,
      endDate: PropTypes.any,
      highlightedPeriods: PropTypes.array,
      weekHighlightInfo: PropTypes.object,
      onKeyDown: PropTypes.func,
      onClose: PropTypes.func,
      onSelect: PropTypes.func,
      isSingleDate: PropTypes.bool
    }
  }

  static get defaultProps () {
    return {
      active: false,
      startDate: '',
      endDate: '',
      highlightedPeriods: [],
      weekHighlightInfo: {},
      onKeyDown: null,
      onClose: null,
      onSelect: null,
      isSingleDate: false
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillUpdate (nextProps, nextState) {
    if (!this.state.active && nextState.active) {
      document.addEventListener('click', this._documentListener)
    }
  }

  componentDidUpdate (prevProps, prevState) {
    if (prevState.active && !this.state.active) {
      document.removeEventListener('click', this._documentListener, false)
    }
  }

  componentWillUnmount () {
    if (this.state.active) {
      document.removeEventListener('click', this._documentListener, false)
    }
  }

  prevMonth () {
    const date = new Date(this.state.date)
    date.setMonth(date.getMonth() - 1)
    this.setState({ date: date.getTime() })
  }

  nextMonth () {
    const date = new Date(this.state.date)
    date.setMonth(date.getMonth() + 1)
    this.setState({ date: date.getTime() })
  }

  setRange (selectionStart = 0, selectionEnd = 0) {
    this.setState({ selectionStart, selectionEnd })
  }

  show () {
    this.setState({ active: true })
  }

  hide () {
    this.setState({ active: false })
  }

  render () {
    const {
      date,
      selectionStart,
      selectionEnd,
      active,
      startDate,
      endDate
    } = this.state
    const { isSingleDate } = this.props
    const className = `daterangewrap${active ? ' active' : ''}`
    let dateInfo = ''

    let startData = ''
    let endData = ''

    if (selectionStart > 0 && !selectionEnd) {
      startData = DateUtil.getDisplayDate(selectionStart)

      dateInfo = `${startData} - ${startData}`
    } else if (selectionStart > 0 && selectionStart > 0) {
      startData = DateUtil.getDisplayDate(selectionStart)
      endData = DateUtil.getDisplayDate(selectionEnd)

      dateInfo = `${startData} - ${endData}`
    }

    return (
      <div className='cdaterange-picker'>
        <div className='form-input input'>
          <span className='iconcont'>
            <i className='icon icon-phase-calendar' />
          </span>
          <input
            className='input input-element dropdown-value'
            type='text'
            placeholder={IntlUtil.convertMessage('common.list.fromTo')}
            onClick={this._onClick.bind(this)}
            autoFocus
            onFocus={this._onFocus.bind(this)}
            value={dateInfo}
            onKeyDown={this._onKeyDown.bind(this)}
            onChange={this._onChange.bind(this)}
            onBlur={this._onBlur.bind(this)}
          />
        </div>
        <div className={className}>
          <Header
            date={date}
            prevMonth={this.prevMonth.bind(this)}
            nextMonth={this.nextMonth.bind(this)}
            onClose={this._onClickClose.bind(this)}
          />
          <Calendar
            date={date}
            isSingleDate={isSingleDate}
            indexStart={selectionStart}
            indexEnd={selectionEnd}
            startDate={startDate}
            endDate={endDate}
            highlightedPeriods={this.props.highlightedPeriods}
            weekHighlightInfo={this.props.weekHighlightInfo}
            setRange={this.setRange.bind(this)}
            onSelectSingleDate={this._onSelectSingleDate.bind(this)}
            onSelect={this._onSelect.bind(this)}
          />
        </div>
      </div>
    )
  }

  /* Event Handlers */
  _onClick (_ev) {
    this.setState({ active: true })
  }

  _onSelect (bselect) {
    if (bselect === false) {
      this.setState({ active: bselect })
      if (this.props.onSelect) {
        const selectionStart = this.state.selectionStart
          ? DateUtil.getUTCDateFromDate(this.state.selectionStart)
          : 0
        const selectionEnd = this.state.selectionEnd
          ? DateUtil.getUTCDateFromDate(this.state.selectionEnd)
          : 0

        this.props.onSelect(selectionStart, selectionEnd || null)
      }
    } else {
      this.setState({ startDate: null, endDate: null })
    }
  }

  _onSelectSingleDate (date, ev) {
    if (this.props.onSelect) {
      this.props.onSelect(new Date(date), null, ev)
      this.setState({ active: false, startDate: new Date(date) })
    }
  }

  _onFocus (_ev) {
    this.show()
  }

  _onKeyDown (ev) {
    if (ev.keyCode === 13 || ev.keyCode === 9 || ev.keyCode === 27) {
      this.hide()
      if (this.props.onSelect) {
        this.props.onSelect(
          this.state.selectionStart
            ? DateUtil.getUTCDateFromDate(this.state.selectionStart)
            : 0,
          this.state.selectionEnd
            ? DateUtil.getUTCDateFromDate(this.state.selectionEnd)
            : 0
        )
      }

      ev.preventDefault()
      ev.stopPropagation()
    }

    if (ev.keyCode === 8) {
      if (this.props.onSelect) {
        this.props.onSelect(0, 0)
        this.setState({ selectionStart: 0, selectionEnd: 0 })
      }

      ev.preventDefault()
      ev.stopPropagation()
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(ev)
    }
  }

  _onBlur (ev) {
    if (
      this.state.active &&
      !Util.targetIsDescendant(ev, ReactDOM.findDOMNode(this))
    ) {
      this.hide()
    }
  }

  _handleDocumentClick (ev) {
    if (
      this.state.active &&
      !Util.targetIsDescendant(ev, ReactDOM.findDOMNode(this))
    ) {
      this.hide()

      if (this.props.onClose) {
        this.props.onClose()
      }
    }
  }

  _onChange (_value, _event) {}

  _onClickClose () {
    this.hide()

    if (this.props.onClose) {
      this.props.onClose()
    }
  }
}

export default DateRangePicker
